<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useVuelidate } from "@vuelidate/core";
import { email, helpers, required } from "@vuelidate/validators";
import ModalBase from "@/components/modal/ModalBase.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonPhoneInput from "@/components/ArgonPhoneInput.vue";
import ArgonRadio from "@/components/ArgonRadio.vue";
import ArgonAddressNormalize from "@/components/ArgonAddressNormalize.vue";
import ArgonLoadingButton from "@/components/ArgonLoadingButton.vue";
import { warehouseStore } from "@/_store/warehouseStore";
import { errorUtils } from "@/utils";

const props = defineProps({
  warehouseId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(["close", "success"]);
const vuexStore = useStore();
const warehousesStore = warehouseStore();
const bootstrap = vuexStore.state.bootstrap;
const modalRef = ref(null);
const snackbar = ref(null);
const disableButton = ref(false);

const createDefaultForm = () => ({
  account_id: "",
  name: "",
  type: "PROPIO",
  address: {
    address: "",
    label: "",
    coordenates: null,
    normalized: false,
    postal_code: "",
  },
  time_window: {
    start_time: "",
    end_time: "",
  },
  contact: {
    name: "",
    attention_of: "",
    phone: "",
    email: "",
  },
});

const form = ref(createDefaultForm());

function parseAddress(value) {
  if (value && typeof value === "object") {
    return {
      address: value.address || "",
      label: value.label || "",
      coordenates: value.coordenates || null,
      normalized: !!value.normalized,
      postal_code: value.postal_code || "",
    };
  }
  return createDefaultForm().address;
}

const rules = {
  name: {
    required: helpers.withMessage("Nombre es requerido", required),
  },
  address: {
    address: {
      required: helpers.withMessage("Dirección es requerida", required),
    },
    normalized: {
      validNormalized: helpers.withMessage(
        "Debes validar la dirección",
        (value, address) => {
          const addressText = String(address?.address || "").trim();
          if (!addressText) return true;
          return !!value;
        }
      ),
    },
  },
  time_window: {
    start_time: {
      required: helpers.withMessage(
        "Hora inicio es requerida si indicas hora fin",
        (value, timeWindow) => !!value || !timeWindow?.end_time
      ),
    },
    end_time: {
      required: helpers.withMessage(
        "Hora fin es requerida si indicas hora inicio",
        (value, timeWindow) => !!value || !timeWindow?.start_time
      ),
      afterStart: helpers.withMessage(
        "La hora fin debe ser posterior a la hora inicio",
        (value, timeWindow) => {
          if (!value || !timeWindow?.start_time) return true;
          return value > timeWindow.start_time;
        }
      ),
    },
  },
  contact: {
    email: {
      email: helpers.withMessage("Ingrese un email valido", email),
    },
    phone: {
      valid: helpers.withMessage("Teléfono no es válido", (value) => {
        const trimmed = String(value || "").trim();
        return !trimmed || /^\+\d{7,18}$/.test(trimmed);
      }),
    },
  },
};

const v$ = useVuelidate(rules, form);
const isEdit = computed(() => !!props.warehouseId);
const modalTitle = computed(() => (isEdit.value ? "Modificar" : "Nuevo"));

function fillForm(warehouse) {
  form.value = {
    account_id: warehouse?.account_id || "",
    name: warehouse?.name || "",
    type: warehouse?.type || "PROPIO",
    address: parseAddress(warehouse?.address),
    time_window: {
      start_time: warehouse?.time_window?.start_time || "",
      end_time: warehouse?.time_window?.end_time || "",
    },
    contact: {
      name: warehouse?.contact?.name || "",
      attention_of: warehouse?.contact?.attention_of || "",
      phone: warehouse?.contact?.phone || "",
      email: warehouse?.contact?.email || "",
    },
  };
}

watch(
  () => props.warehouseId,
  (value) => {
    disableButton.value = false;
    fillForm(null);
    v$.value.$reset();
    if (value) {
      warehousesStore.getWarehouseById(value);
    }
  },
  { immediate: true }
);

watch(
  () => warehousesStore.warehouse,
  (value) => {
    if (!props.warehouseId || !value) return;
    if (String(value._id || "") !== String(props.warehouseId)) return;
    fillForm(value);
    v$.value.$reset();
  }
);

watch(
  () => warehousesStore.warehouseCreated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    modalRef.value.closeModal();
    emit("success");
  }
);

watch(
  () => warehousesStore.warehouseUpdated,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    modalRef.value.closeModal();
    emit("success");
  }
);

watch(
  () => warehousesStore.error,
  (value) => {
    if (!value) return;
    disableButton.value = false;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);

onMounted(() => {
  const modal = new bootstrap.Modal(document.getElementById("modalWarehouse"), {
    keyboard: true,
    backdrop: "static",
  });
  modal.show();
});

function closeModal() {
  emit("close");
}

const closeSnackbar = () => {
  snackbar.value = null;
};

async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}

async function saveWarehouse() {
  const errors = await validate();

  if (errors.length > 0) {
    let message = "";
    for (const error of errors) {
      if (!message.includes(`<li>${error.$message}</li>`)) {
        message = `${message}<li>${error.$message}</li>`;
      }
    }

    snackbar.value = {
      title: "Error",
      message: `<ul>${message}</ul>`,
      type: "danger",
      color: "white",
    };
    return;
  }

  disableButton.value = true;
  const payload = {
    name: form.value.name.trim(),
    type: form.value.type,
    address: {
      address: String(form.value.address.address || "").trim(),
      label: String(form.value.address.label || "").trim(),
      coordenates: form.value.address.coordenates || null,
      normalized: !!form.value.address.normalized,
      postal_code: String(form.value.address.postal_code || "").trim(),
    },
    time_window: {
      start_time: String(form.value.time_window.start_time || "").trim(),
      end_time: String(form.value.time_window.end_time || "").trim(),
    },
    contact: {
      name: String(form.value.contact.name || "").trim(),
      attention_of: String(form.value.contact.attention_of || "").trim(),
      phone: String(form.value.contact.phone || "").trim(),
      email: String(form.value.contact.email || "").trim(),
    },
  };

  if (isEdit.value) {
    warehousesStore.updateWarehouse(payload, props.warehouseId);
    return;
  }
  warehousesStore.createWarehouse(payload);
}
</script>

<template>
  <modal-base id="modalWarehouse" ref="modalRef" width="42rem" @hide="closeModal">
    <template #header>
      <h6 class="modal-title mb-0">
        {{ modalTitle }}
      </h6>
    </template>

    <template #body>
      <div class="modal-form--wide-labels">
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="warehouse-name" class="col-form-label required">Nombre</label>
          </div>
          <div class="col">
            <argon-input
              id="warehouse-name"
              v-model="form.name"
              :error="v$.name.$error"
              :is-required="true"
              :max-length="60"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label
              v-tooltip="'Propio: tus propios almacenes. Externo: puede ser la plataforma/hub de un tercero, o la dirección habitual de un cliente.'"
              class="col-form-label required"
              >Tipo <sup class="text-primary sup-question">?</sup></label
            >
          </div>
          <div class="col">
            <div class="d-flex gap-4">
              <argon-radio
                id="warehouse-type-propio"
                v-model="form.type"
                name="PROPIO"
                group-name="warehouse-type"
                :disabled="isEdit"
              >
                Propio
              </argon-radio>
              <argon-radio
                id="warehouse-type-externo"
                v-model="form.type"
                name="EXTERNO"
                group-name="warehouse-type"
                :disabled="isEdit"
              >
                Externo
              </argon-radio>
            </div>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="warehouse-address" class="col-form-label required">Dirección</label>
          </div>
          <div class="col">
            <argon-address-normalize
              id="warehouse-address"
              v-model="form.address"
              :error="
                v$.address.address.$error || v$.address.normalized.$error
              "
              label-input-id="warehouse-address-label"
              :label-max-length="180"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label class="col-form-label">Horario</label>
          </div>
          <div class="col">
            <div class="row g-2">
              <div class="col-6">
                <argon-input
                  id="warehouse-time-window-start"
                  v-model="form.time_window.start_time"
                  type="time"
                  :error="v$.time_window.start_time.$error"
                />
              </div>
              <div class="col-6">
                <argon-input
                  id="warehouse-time-window-end"
                  v-model="form.time_window.end_time"
                  type="time"
                  :error="v$.time_window.end_time.$error"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="warehouse-contact-name" class="col-form-label">Contacto</label>
          </div>
          <div class="col">
            <argon-input
              id="warehouse-contact-name"
              v-model="form.contact.name"
              :max-length="60"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="warehouse-contact-phone" class="col-form-label">Teléfono</label>
          </div>
          <div class="col">
            <argon-phone-input
              id="warehouse-contact-phone"
              v-model="form.contact.phone"
              :error="v$.contact.phone.$error"
            />
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <label for="warehouse-contact-email" class="col-form-label">Email</label>
          </div>
          <div class="col">
            <argon-input
              id="warehouse-contact-email"
              v-model="form.contact.email"
              type="email"
              :error="v$.contact.email.$error"
              :max-length="75"
            />
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <argon-loading-button
        class="btn bg-gradient-primary btn-xs"
        color="primary"
        variant="gradient"
        :label="disableButton ? 'Guardando...' : 'Guardar'"
        :loading="disableButton"
        :disable-button="disableButton"
        @click="saveWarehouse"
      />
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>

